// Pure layout + export helpers for the table primitive. Kept out of table.jsx
// so the column-sizing and CSV logic is unit-testable without a DOM.

// Per-column min width (px). An explicit numeric width wins; otherwise a
// type-based floor keeps columns legible and lets a wide table overflow into
// horizontal scroll instead of crushing every column to a sliver.
export function colMinPx(c) {
    if (typeof c.width === "number") return c.width;
    if (c.width === "auto") return 80;
    if (c.spark) return 130;
    if (c.link) return 140;
    const numeric = c.format === "number" || c.format === "integer" || c.format === "percent"
        || c.format === "0.00" || c.format === "0.0" || c.format === "duration_ms" || c.format === "duration_s";
    return numeric ? 76 : 120;
}

// grid-template-columns string: explicit px/auto passthrough, else a
// minmax(<floor>px, <weight>fr) track -- fills the width when there's room, holds
// the floor (and overflows to scroll) when there isn't. A column's optional
// `weight` (default 1) is the fr multiplier, so a text-heavy column can claim a
// larger share of the free space than its neighbours without a fixed width.
export function gridTemplate(columns) {
    return columns.map((c) => {
        if (c.width === "auto") return "auto";
        if (typeof c.width === "number") return `${c.width}px`;
        const fr = typeof c.weight === "number" && c.weight > 0 ? c.weight : 1;
        return `minmax(${colMinPx(c)}px, ${fr}fr)`;
    }).join(" ");
}

// Sum of column floors — the min width the grid must reach before it scrolls.
export function totalMinWidth(columns) {
    return columns.reduce((sum, c) => sum + colMinPx(c), 0);
}

// Order two cell values without being told their type. A kernel column carries a
// display `format`, not a sort type, and the same column can arrive as a number,
// a numeric string, or an ISO timestamp depending on the query -- so the shape of
// the value decides. Numbers before dates: an ISO timestamp is never a finite
// Number, while an epoch-like count would wrongly parse as a date.
export function compareCells(a, b) {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && typeof a !== "boolean" && typeof b !== "boolean") {
        return na - nb;
    }
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
    // `numeric` so host-7 sorts before host-10 rather than lexically after it.
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

const isBlank = (v) => v == null || v === "";

// Sort rows by one column, newest/largest first when `dir` is "desc".
//
// Blanks always sink, in BOTH directions: an endpoint with no last-seen is not
// the most recent one just because the reader flipped the arrow, and a table
// whose first screenful is empty cells reads as broken. Returns a new array so
// the caller's memo dependency stays honest.
export function sortRows(rows, sort, getRow = (row) => row) {
    if (!sort?.id) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((ra, rb) => {
        const a = getRow(ra)?.[sort.id];
        const b = getRow(rb)?.[sort.id];
        const ba = isBlank(a);
        const bb = isBlank(b);
        if (ba || bb) return ba && bb ? 0 : (ba ? 1 : -1);
        return dir * compareCells(a, b);
    });
}

function mergeValue(current, incoming, operation) {
    if (operation === "sum") {
        const a = Number(current);
        const b = Number(incoming);
        return (Number.isFinite(a) ? a : 0) + (Number.isFinite(b) ? b : 0);
    }
    if (isBlank(current)) return incoming;
    if (isBlank(incoming)) return current;
    const comparison = compareCells(incoming, current);
    if (operation === "min") return comparison < 0 ? incoming : current;
    if (operation === "max") return comparison > 0 ? incoming : current;
    return current;
}

// Merge a bounded display result after a kernel has normalized its labels.
// The contract is explicit per field so the renderer never guesses whether a
// duplicate count is additive. This is presentation shaping only: linked ids
// and the original analytics query remain the kernel's responsibility.
export function mergeTableRows(rows, merge) {
    if (!Array.isArray(rows) || !merge?.by || !merge.fields) return rows;
    const by = merge.by;
    const fields = merge.fields;
    const grouped = new Map();
    for (const row of rows) {
        const key = row?.[by];
        if (!grouped.has(key)) {
            grouped.set(key, { ...row });
            continue;
        }
        const target = grouped.get(key);
        for (const [field, operation] of Object.entries(fields)) {
            target[field] = mergeValue(target[field], row?.[field], operation);
        }
    }
    return [...grouped.values()];
}

function csvEscape(v) {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Build a CSV string from the FULL row set (not the maxRows-capped render).
// Raw values are emitted (not display-formatted) so numbers stay numeric for a
// spreadsheet; a sparkline column's per-row array flattens to a ;-joined series.
export function buildCsv(columns, rows) {
    const lines = [columns.map((c) => csvEscape(c.label || c.id)).join(",")];
    for (const row of rows) {
        lines.push(columns.map((c) => {
            const v = row?.[c.spark?.field || c.id];
            return csvEscape(Array.isArray(v) ? v.join(";") : v);
        }).join(","));
    }
    return lines.join("\n");
}
